import React, { useEffect, useState, useContext } from "react";
import { LayerItemId } from "../types/LayerItemId";
import { useHighlightLayer } from "../hooks/useHighlightLayer";
import { useGeoApps } from "@mapgear/geoapps-ui-framework";
import { FeatureInfoContext } from "./FeatureInfoContext";
import { PlotDataType } from "../types/PlotDataType";

export const Popup = (): JSX.Element => {
    const [toggleState, setToggleState] = useState<boolean>(false);
    const { featureInfo, setFeatureInfo, helpLayer } = useContext(FeatureInfoContext);
    const { goToSelected, clearFeatures } = useHighlightLayer();
    const [nr, setNr] = useState<string>();
    const [plotData, setPlotData] = useState<PlotDataType>();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingFailed, setLoadingFailed] = useState<boolean>(false);
    const [layers, setLayers] = useState<any[]>([]);
    const { geoapps } = useGeoApps();
    const map = geoapps.GetMap("map");

    /**
     * Toggle popup when a feature is clicked
     * Get feature nr on click
     */
    useEffect(() => {
        if (layers.length === 0) {
            const plotLayer = map.Layers.Items.filter(layer => layer.LayerItemId === LayerItemId.PLOTS);
            if (plotLayer.length === 0) return;
            setLayers(plotLayer[0].ChildLayers);
        }
        else {
            map.Layers.onClicked.add(e => {
                // Retrieve feature info of clicked feature
                const info = e.Popups.filter(popup => popup.Title === "percelen");
                if (info.length === 0) return;
                setNr(info["0"].Tabs["0"].Attributes[4].Value);

                if (!toggleState) {
                    setToggleState(true)
                }
            })
        }
    }, [layers]);

    /**
     * Fetch plot data
     */
    useEffect(() => {
        if (!featureInfo.nr) return;

        if (!toggleState) {
            setToggleState(true);
        }

        if (featureInfo.nr !== nr) {
            setNr(featureInfo.nr);
        }

        setLoading(true);

        fetch(`https://bonmama.nl/atlasavereest.php?kad=${featureInfo.nr}`)
            .then(res => {
                if (res.ok) {
                    return res.json()
                }
            })
            .then(data => {
                setPlotData(data);
                setLoading(false);
                setLoadingFailed(false);
            })
            .catch(() => {
                setLoading(false);
                setLoadingFailed(true);
            });

    }, [featureInfo.nr]);

    // Get correct feature info when changing plot number
    useEffect(() => {
        if (!nr || layers.length === 0 || loading) return;

        setLoading(true);

        // Build filter object
        const filter = {
            operator: "=",
            attribute: "nr",
            value: nr.toUpperCase(),
        };

        // Get feature info
        layers[0].GetFeaturesByFilter2(filter).then(res => {
            if (res.length === 0) {
                setFeatureInfo?.({
                    nr: nr,
                    geom: undefined,
                    from: undefined,
                    until: undefined,
                    kt: undefined,
                    toggle: 0,
                })
            } else {
                const featureInfoObj = {
                    nr: res[0].OlFeature.getProperties().nr,
                    geom: res[0].GetGeometry().AsGeoJSON(),
                    from: res[0].OlFeature.getProperties().van,
                    until: res[0].OlFeature.getProperties().tot,
                    kt: res[0].OlFeature.getProperties().kt,
                    toggle: featureInfo.toggle + 1,
                }
                setFeatureInfo?.(featureInfoObj);
                goToSelected(featureInfoObj);
                                
                // Remove geoapps highlight 
                map.MapEngine.Map.Interactions.RemoveHighlight();
            }
        })

        // Set the url param to the new plot number
        const url = new URL(window.location.href);
        url.searchParams.set("kad", nr);
        window.history.pushState({}, '', url.toString());

    }, [nr]);

    /**
     * Toggle the popup visibility
     * @param state boolean to open or close the popup with
     */
    const handleToggle = (state: boolean) => {
        setToggleState(state);

        // Remove highlight when closing the popup
        if (!state) {
            clearFeatures();
        }
    };

    /**
     * Adds the corresponding help map
     */
    const addHelpMap = () => {
        setFeatureInfo?.((prevState) => ({ ...prevState, toggle: featureInfo.toggle + 1 }));
    };

    /**
     * Removes the corresponding help map
     */
    const removeHelpMap = () => {
        helpLayer.setSource(null);
    };

    /**
     * Builds text of the selected plot.
     * @params data The data of the plot object
     * @returns popup content as jsx
     */
    const getPlotText = (data: PlotDataType) => {
        if (!data) return;
        return (
            <>
                {data.onstaan.length > 0 &&
                    <>
                        <br /><b>{data.van}</b> ontstaan uit
                        {data.onstaan.map(nr => {
                            return <a style={{ color: "#0078A8", cursor: "pointer" }} onClick={() => updateNr(nr)}> {nr}</a>
                        })
                        }
                    </>
                }
                {data.leggers.map(info => {
                    return (
                        <>
                            <br /><b>{info.jaar}</b> {info.soort}: {info.hbsnLink ? <a style={{ color: "#0078A8", cursor: "pointer" }} onClick={() => window.open(info.hbsnLink!)}>{info.voornaam + " " + info.achternaam}</a> : info.voornaam + " " + info.achternaam} {info.woonplaats}{info.beroep ? <>, {info.beroep}</> : <></>}
                        </>
                    )
                })
                }
                {data.mutatie.length > 0 && (
                    <>
                        <br /><b>{data.tot}</b> overgegaan in
                        {data.mutatie.map(nr => {
                            return <a style={{ color: "#0078A8", cursor: "pointer" }} onClick={() => updateNr(nr)}> {nr}</a>
                        })}
                    </>
                )}
            </>
        )
    };

    /**
     * Updates the plot number
     * @param nr The new plot number
     */
    const updateNr = (nr: string) => {
        const updatedNr = nr.replace(/([a-zA-Z])0+(\d+)/g, '$1$2');
        setNr(updatedNr);
    };

    /**
     * Create popup title
     * @param plotData The plot data to make the popup title from
     * @returns the popup title
     */
    const getPopupTitle = (plotData: PlotDataType) => {
        let title: string = `Perceel Avereest sectie ${plotData.sectie} no ${plotData.number}`;

        if (plotData.oppervlakte) {
            title += ` - ${plotData.oppervlakte.toLocaleString("nl-NL")}m2`;
        }

        if (plotData.soort) {
            title += ` - ${plotData.soort}`;
        }

        return (
            title
        )
    };

    /**
     * Builds the content for the popup
     * @returns Popup content
     */
    const getContent = () => {

        if (loading) {
            return <div>Laden...</div>
        }
        else if (loadingFailed) {
            return <div>Er is iets misgegaan</div>
        }
        else if (!loading && !plotData) {
            return <a>Selecteer punt</a>
        }
        else if (!loading && !loadingFailed && plotData) {
            return (
                <>
                    <b>{getPopupTitle(plotData)}</b>
                    <br />
                    <button onClick={() => { window.open(`http://www.bonmama.nl/avkad.php?kad=${featureInfo.nr}`) }} className="mybtn" title="Filiatie">
                        <i className="fa fa-eye"></i>
                    </button>
                    {featureInfo.geom ?
                        <>
                            <button onClick={() => addHelpMap()} className="mybtn" title="Toon hulpkaart">
                                <i className="far fa-map"></i>
                            </button>
                            <button onClick={() => removeHelpMap()} className="mybtn" title="Verberg hulpkaart">
                                <i className="fa fa-trash"></i>
                            </button>
                            <button onClick={() => goToSelected(featureInfo)} className="mybtn" title="Ga naar perceel">
                                <i className="fa fa-exchange"></i>
                            </button>
                        </> :
                        <div>&#91;geen kaart beschikbaar&#93;</div>}
                    {getPlotText(plotData)}
                </>
            )
        }
    };

    return (
        <div className="popup-box">
            {toggleState ?
                <div className={toggleState ? "popup-content" : "popup-content is-hidden"}>
                    {getContent()}
                    <a className="popup-close-btn" onClick={() => { handleToggle(false); map.MapEngine.Map.Interactions.RemoveHighlight() }}>
                        x
                    </a>
                </div> :
                <div className={toggleState ? "popup-info is-hidden" : "popup-info"} onClick={() => handleToggle(true)}>
                    <i className="fa-solid fa-info"></i>
                </div>
            }
        </div>
    );
};
